import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { Icon } from "@iconify/react";
import moment from "moment";

function MailServerReport({ data, pageTitle }) {
  return (
    <Paper elevation={3} sx={{ padding: 3, width: "100%", maxWidth: 900, margin: "0 auto" }}>
      <Box>
        {/* Title & Target Domain */}
        <Typography variant="h4" fontWeight="bold" mb={3} color="rgb(0, 145, 70)" sx={{ textAlign: "center" }}>
          {pageTitle}
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <Icon icon="mdi:web" width={24} height={24} style={{ marginRight: 8 }} />
            Target:
          </Typography>
          <Chip label={data?.domain || data?.target} color="primary" variant="outlined" sx={{ fontSize: "1rem", padding: "5px" }} />
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="body1" sx={{ color: "#777", mb: 2 }}>
            Report Date: {moment(data?.date).format("MMM DD, YYYY HH:mm")}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />



        {/* Grades Summary */}
        <Card sx={{ mt: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Grades Summary
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(data.grades).map(([key, value]) => (
                key !== "reason" && (
                  <Grid item xs={6} sm={3} key={key} sx={{textTransform:'capitalize'}}>
                    <Chip label={`${key.replace("Grade", "")}: ${value}`} color="success" sx={{ fontSize: "1rem", fontWeight: "bold" }} />
                  </Grid>
                )
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Server Latency */}
        <Card sx={{ mt: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Server Latency
            </Typography>
            <Typography variant="body1">
              {data.result?.serverLatency ? `${data.result.serverLatency}` : "No latency data available"}
            </Typography>
          </CardContent>
        </Card>

        {/* Security Warnings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Security Warnings & Recommendations
            </Typography>
            <List dense>
              {data.grades?.reason?.map((reason, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`⚠️ ${reason}`} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* PTR Records */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              PTR Info
            </Typography>
            <List dense>
              {data.result?.ptrInfo?.map((reason, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`${reason}`} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* MX Records */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              MX Records
            </Typography>
            <List dense>
              {data.result?.newMxRecords?.mxRecords.length > 0 ? (
                data.result.newMxRecords.mxRecords.map((record, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={`📡 ${record}`} />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No MX records found
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>

        {/* Mail Blacklist Info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Mail Blacklist Information
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>IP</strong></TableCell>
                    <TableCell><strong>Blacklisted</strong></TableCell>
                    <TableCell><strong>Detected By</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.result?.mailBlackListInfo.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>{entry.ip}</TableCell>
                      <TableCell>
                        <Chip label={entry.blacklisted ? "Yes" : "No"} color={entry.blacklisted ? "error" : "success"} />
                      </TableCell>
                      <TableCell>{entry["detected-by"].length > 0 ? entry["detected-by"].join(", ") : "None"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* SPF, DKIM, DMARC, BIMI, TLS-RPT, MTA-STS Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Email Security Records (SPF, DKIM, DMARC, BIMI, TLS-RPT, MTA-STS)
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Record</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Warnings</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["spfInfo", "dkimInfo", "dmarcInfo", "bimiInfo", "tlsrptInfo", "mtastsInfo"].map((recordType, index) => (
                    <TableRow key={index}>
                      <TableCell>{recordType.replace("Info", "").toUpperCase()}</TableCell>
                      <TableCell>{data.result[recordType]?.[`${recordType}Record`] || "Not found"}</TableCell>
                      <TableCell>{data.result[recordType]?.status || "Unknown"}</TableCell>
                      <TableCell>
                        {data.result[recordType]?.warnings?.length > 0 ? (
                          <List>
                            {data.result[recordType]?.warnings.map((warning, i) => (
                              <ListItem key={i} sx={{ padding: 0 }}>
                                <ListItemText primary={`⚠️ ${warning}`} />
                              </ListItem>
                            ))}
                          </List>
                        ) : "No warnings"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

      </Box>
    </Paper>
  );
}

export default MailServerReport;
